import getChildrenSelectedCategories from './getChildrenSelectedCategories'
import { getNumberOfChildrenTreatedAsAdults } from './getNumberOfChildrenTreatedAsAdults'
import { getNumberOfChildrenWithPercentageValues } from './getNumberOfChildrenWithPercentageValues'
import { getPaxForPersons, getTotalRateBeforeDiscount } from './getTotalRate'
import getOBPChildrenPrice from './getOBPChildrenPrice'
import getPPPDChildrenPrice from './getPPPDChildrenPrice'
import getExcludedServicesTotal from './getExcludedServicesTotal'
import getAllAdultsRate, { capChildrenPrice } from './getAllAdultsRate'
import { getTouristTax } from 'Rentlio/components/ReservationSummary/util/getTouristTax'
import { PROMO_CODE_DISCOUNT_TYPE_ENUM } from 'Rentlio/utils/promoCodeDiscountTypeEnum'

const getTotalOBPRateDiscounted = (ratePlan, adults, childrenAges, unitTypeOccupancy) => {
  const { childrenCategories } = ratePlan
  let adultsNumber = adults || 0
  const childrenSelectedCategories = getChildrenSelectedCategories(childrenAges, childrenCategories)
  // because value type nominal for children we are not counting inside pax, we are adding it separately
  const numberOfChildrenTreatedAsAdults = getNumberOfChildrenTreatedAsAdults(childrenSelectedCategories)
  adultsNumber += numberOfChildrenTreatedAsAdults

  const persons = adultsNumber + getNumberOfChildrenWithPercentageValues(childrenSelectedCategories)

  const paxPriceForAllPersons = getPaxForPersons(ratePlan, persons)
    ? getPaxForPersons(ratePlan, persons).totalRateBeforeDiscount
    : 0
  const paxPriceForAdults = getPaxForPersons(ratePlan, adultsNumber)
    ? getPaxForPersons(ratePlan, adultsNumber).totalRateBeforeDiscount
    : 0

  const childrenPrice = getOBPChildrenPrice(
    childrenSelectedCategories,
    (paxPriceForAllPersons - paxPriceForAdults) / (persons - adultsNumber),
    ratePlan.nights
  )

  const allAdultsRate = getAllAdultsRate(ratePlan, unitTypeOccupancy, adults, childrenAges, 'totalRateBeforeDiscount')

  const totalBeforeDiscount = paxPriceForAdults + capChildrenPrice(childrenPrice, paxPriceForAdults, allAdultsRate)

  if (ratePlan.isTouristTaxIncluded) {
    const touristTaxSum = getTouristTax(ratePlan.includedTouristTaxes, adults, childrenAges)
    return (
      applyPromoDiscount(
        totalBeforeDiscount - touristTaxSum,
        ratePlan.activePromoCode.discountType,
        ratePlan.activePromoCode.discountAmount
      ) + touristTaxSum
    )
  } else {
    return applyPromoDiscount(
      totalBeforeDiscount,
      ratePlan.activePromoCode.discountType,
      ratePlan.activePromoCode.discountAmount
    )
  }
}

const getTotalPPPDRateDiscounted = (
  {
    nights,
    totalRate,
    pricePerPerson,
    childrenCategories,
    activePromoCode,
    totalRateBeforeDiscount,
    isTouristTaxIncluded,
    includedTouristTaxes
  },
  adults,
  childrenAges
) => {
  let discountedTotalRate = 0

  if (isTouristTaxIncluded) {
    const touristTaxSum = getTouristTax(includedTouristTaxes, adults, childrenAges)

    // discount must not be applyed on tourist tax, so first we are removing tourist tax, then apply discount and after that again add tourist tax
    discountedTotalRate =
      applyPromoDiscount(
        totalRateBeforeDiscount - touristTaxSum,
        activePromoCode.discountType,
        activePromoCode.discountAmount
      ) + touristTaxSum
  } else {
    discountedTotalRate = totalRate
  }

  const childrenSelectedCategories = getChildrenSelectedCategories(childrenAges, childrenCategories)
  const childrenPrice = getPPPDChildrenPrice(childrenSelectedCategories, pricePerPerson, nights)

  const adultsNumber = adults || 0
  const adultPrice = adultsNumber * pricePerPerson * nights

  let peoplePriceSum = adultPrice + childrenPrice

  if (activePromoCode.discountType === PROMO_CODE_DISCOUNT_TYPE_ENUM.PERCENTAGE) {
    peoplePriceSum = applyPromoDiscount(peoplePriceSum, activePromoCode.discountType, activePromoCode.discountAmount)
  } else if (activePromoCode.discountType === PROMO_CODE_DISCOUNT_TYPE_ENUM.NOMINAL) {
    const remainingDiscount = Number(activePromoCode.discountAmount) - (totalRateBeforeDiscount - discountedTotalRate)
    peoplePriceSum = applyPromoDiscount(peoplePriceSum, activePromoCode.discountType, remainingDiscount)
  }

  return discountedTotalRate + peoplePriceSum
}

const getTotalPerRoomRateDiscounted = (ratePlan, adults, childrenAges) => {
  if (ratePlan.isTouristTaxIncluded) {
    const touristTaxSum = getTouristTax(ratePlan.includedTouristTaxes, adults, childrenAges)

    return (
      applyPromoDiscount(
        ratePlan.totalRateBeforeDiscount - touristTaxSum,
        ratePlan.activePromoCode.discountType,
        ratePlan.activePromoCode.discountAmount
      ) + touristTaxSum
    )
  }

  return applyPromoDiscount(
    ratePlan.totalRateBeforeDiscount,
    ratePlan.activePromoCode.discountType,
    ratePlan.activePromoCode.discountAmount
  )
}

const applyPromoDiscount = (amount, discountType, discountAmount) => {
  if (!discountType || !discountAmount) {
    return amount
  }

  if (discountType === PROMO_CODE_DISCOUNT_TYPE_ENUM.PERCENTAGE) {
    return amount * (1 - Number(discountAmount) / 100)
  } else if (discountType === PROMO_CODE_DISCOUNT_TYPE_ENUM.NOMINAL) {
    return Math.max(amount - discountAmount, 0)
  }
}

const getTotalRateDiscounted = (unitTypeOccupancy, ratePlan, adults, childrenAges) => {
  let discountedRateTotal = 0
  let remainingNominalDiscount = 0

  if (ratePlan.isPaxRate) {
    discountedRateTotal = getTotalOBPRateDiscounted(ratePlan, adults, childrenAges, unitTypeOccupancy)
  } else if (ratePlan.isPPPDRate) {
    discountedRateTotal = getTotalPPPDRateDiscounted(ratePlan, adults, childrenAges)
  } else {
    discountedRateTotal = getTotalPerRoomRateDiscounted(ratePlan, adults, childrenAges)
  }

  if (ratePlan.activePromoCode?.discountType === PROMO_CODE_DISCOUNT_TYPE_ENUM.NOMINAL) {
    // We want just the rate price without services price and getTotalRateBeforeDiscount returns the price sum containing excluded services
    // occupancy is passed for pax rates only, where the children ceiling has to be applied on the same
    // basis as the discounted total - per room rates would otherwise also pick up the deprecated extra
    // person and children charges, which getTotalPerRoomRateDiscounted does not include
    const rateTotalBeforeDiscount =
      getTotalRateBeforeDiscount(ratePlan.isPaxRate ? unitTypeOccupancy : {}, ratePlan, adults, childrenAges) -
      getExcludedServicesTotal(ratePlan, adults, childrenAges)

    remainingNominalDiscount = Math.max(
      ratePlan.activePromoCode.discountAmount - (rateTotalBeforeDiscount - discountedRateTotal),
      0
    )
  }

  return (
    discountedRateTotal +
    Math.max(getExcludedServicesTotal(ratePlan, adults, childrenAges) - remainingNominalDiscount, 0)
  )
}

export default getTotalRateDiscounted
